import { Controller } from "@hotwired/stimulus"

// Reveals a detail row that sits immediately after the row this controller is mounted on.
//
// Table rows cannot be wrapped in a container element, so a controller cannot hold both the
// trigger and the detail: the two live in sibling <tr>s. This one is mounted on the summary row
// and walks to its next sibling instead of using targets.
//
// Connects to data-controller="table-row-disclosure"
//
//   <tr data-controller="table-row-disclosure">
//     <td data-action="click->table-row-disclosure#toggle"
//         data-table-row-disclosure-target="trigger">…</td>
//   </tr>
//   <tr class="hidden" data-table-row-disclosure-target="detail">…</tr>
//
// The detail row is rendered hidden, so a page with JavaScript unavailable shows the original
// table rather than every row expanded at once.
// Read off the DOM rather than through Stimulus targets: the detail row is a SIBLING of this
// controller's element, so it sits outside the subtree Stimulus scopes targets to.
const DETAIL_ATTRIBUTE = "data-table-row-disclosure-target"
const DETAIL_TARGET = "detail"

export default class extends Controller {
  static targets = ["trigger", "indicator"]

  static classes = ["hidden"]

  // The sibling must identify itself as the detail row. A summary row can be rendered without one
  // — a destination with no rule outcome still has API rates worth a row — and claiming whatever
  // follows would hide the NEXT destination's summary row instead.
  connect() {
    this.detailRow = this.findDetailRow()
    this.setExpanded(false)
  }

  findDetailRow() {
    const sibling = this.element.nextElementSibling
    if (!sibling) return null

    const declared = sibling.getAttribute(DETAIL_ATTRIBUTE)?.split(/\s+/) || []

    return declared.includes(DETAIL_TARGET) ? sibling : null
  }

  toggle() {
    if (!this.detailRow) return

    this.setExpanded(this.detailRow.classList.contains(this.hiddenClassName))
  }

  setExpanded(expanded) {
    if (!this.detailRow) return

    this.detailRow.classList.toggle(this.hiddenClassName, !expanded)

    // Rotating the same glyph rather than swapping icons keeps the column width stable as rows
    // open and close.
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.style.transform = expanded ? "rotate(90deg)" : "rotate(0deg)"
    }

    this.triggerTargets.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", expanded ? "true" : "false")
    })
  }

  // Falls back to Tailwind's `hidden` when the caller does not name a class, so the markup does
  // not have to repeat it.
  get hiddenClassName() {
    return this.hasHiddenClass ? this.hiddenClass : "hidden"
  }
}
